import { useEffect } from 'react';
import Article from '@/components/article/Article';
import ArticleMobile from '@/components/article/ArticleMobile';
import ArticleNotFound from '@/components/article/ArticleNotFound';
import { useUserAgentInfo } from '@/context/user-agent';
import {
  getDocDetail,
  getProductList,
  getProductOnlineDocs,
} from '@/services/document';
import type { DocInfo, ErrorResponse, ProductInfo } from '@/services/document';
import type { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import ArticleNoContent from '@/components/article/ArticleNoContent';
import ArticleLoading from '@/components/article/ArticleLoading';
import { getKconfContent } from '@/services/common';
import { defaultCmsConfig } from '@/utils/config';

type DocumentProps = DocInfo & {
  topDocs: ProductInfo[];
  cmsConfig: {
    hotSearchKeys: string[];
    productTypeOrder: string[];
  };
  docsKey: string;
  status: DocStatus;
};
enum DocStatus {
  OK = 200,
  NoContent = 204,
}

let cachedProductList = null;
let cachedCmsConfig = null;

const getProductListCached = async (): Promise<ProductInfo[]> => {
  if (cachedProductList) {
    return cachedProductList;
  } else {
    process.stdout.write(`fetching productList...\n`);
    const productList = await getProductList();
    cachedProductList = productList;
    return productList;
  }
};

// popover 用
const getCmsConfig = async (): Promise<{
  hotSearchKeys: string[];
  productTypeOrder: string[];
}> => {
  if (cachedCmsConfig) {
    return cachedCmsConfig;
  } else {
    cachedCmsConfig = await getKconfContent(
      'website_cms_center',
      'platform_web',
      'streamlake'
    );
    if (!cachedCmsConfig || Object.keys(cachedCmsConfig).length === 0) {
      cachedCmsConfig = defaultCmsConfig;
    }
    return cachedCmsConfig;
  }
};

export const getStaticPaths: GetStaticPaths = async (ctx) => {
  // When this is true (in preview environments) don't
  // prerender any static pages
  // (faster builds, but slower initial page load)
  if (process.env.SKIP_BUILD_STATIC_GENERATION) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }
  const products = await getProductListCached(); // /docs-manage/hot-docs
  const paths = [];
  for (const product of products) {
    const docs = await getProductOnlineDocs({
      productCode: product.productCode,
    });
    if (docs && docs.length) {
      const pathGroup = docs.map((doc) => ({
        params: {
          productCode: product.productCode,
          docsKey: [doc.docId],
        },
      }));
      paths.push(...pathGroup);
    } else {
      paths.push({ params: { productCode: product.productCode, docsKey: [] } });
    }
  }

  process.stdout.write(`generate ${paths.length} pages\n`);
  for (const path of paths) {
    process.stdout.write(
      `/document/${path.params.productCode}/${path.params.docsKey[0] || ''}\n`
    );
  }
  return {
    fallback: true,
    paths: paths,
  };
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  console.log(ctx);
  const docsKey = ctx.params.docsKey?.[0];
  process.stdout.write(
    `fetching data: productCode ${ctx.params.productCode} docsKey ${docsKey}\n`
  );
  // check if docsKey is valid, avoiding redundant api requests
  const regex = /^[a-zA-Z0-9]+$/;
  if (!regex.test(docsKey as string)) {
    return {
      notFound: true,
    };
  }

  const productList = await getProductListCached();
  const cmsConfig = await getCmsConfig();

  if (!docsKey) {
    return {
      props: {
        topDocs: productList,
        status: DocStatus.NoContent,
      },
    };
  }
  const allPageData = await getDocDetail(docsKey); // /docs-manage/doc/:docId

  // to handle error cases
  if ((allPageData as ErrorResponse).code) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      ...allPageData,
      cmsConfig,
      docsKey,
      topDocs: productList,
      status: DocStatus.OK,
    },
  };
};

const CACHE_SIZE = 5;
const CACHE_KEY = 'STREAMLAKE_RECENTLY_VIEWD';
function Document(props: DocumentProps) {
  const { isMobile } = useUserAgentInfo();
  const router = useRouter();
  useEffect(() => {
    if (props.document) {
      const currentItem = {
        code: props.product.productCode,
        name: props.product.productName,
        key: props.docsKey,
      };
      const cache = JSON.parse(localStorage.getItem(CACHE_KEY));
      if (!cache) {
        localStorage.setItem(CACHE_KEY, JSON.stringify([currentItem]));
      } else {
        const index = cache.findIndex(
          (item) => item.code === props.product.productCode
        );
        if (index >= 0) {
          cache.unshift(cache.splice(index, 1)[0]);
        } else {
          cache.unshift(currentItem);
        }
        if (cache.length > CACHE_SIZE) {
          cache.pop();
        }
        localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
      }
    }
  }, []);

  if (router.isFallback) {
    return <ArticleLoading />;
  }
  if (props.status === DocStatus.NoContent) {
    return (
      // 暂无文档
      <ArticleNoContent productList={props.topDocs} />
    );
  }
  return isMobile ? <ArticleMobile {...props} /> : <Article {...props} />;
}

export default Document;
